import { useMemo } from 'react';
import { useUserContext } from 'contexts/user';
import { getComplianceRequirementFromCookies, shouldScrambleForProvider } from 'utils/compliance';
import { ComplianceRequirement } from 'types/compliance';
import { Provider, StandardStatus } from '@zoocasa/go-search';

export function getHideSquareFootage(providerId: number, standardStatus: StandardStatus) {
  return providerId === Provider.PROVIDER_TRIAD_MULTIPLE_LISTING_SERVICE && (standardStatus === StandardStatus.STANDARD_STATUS_ACTIVE || standardStatus === StandardStatus.STANDARD_STATUS_PENDING);
}

/**
 * Hook to reactively compute compliance for listings based on user context.
 * This hook recalculates compliance whenever user authentication state or accepted terms change.
 * 
 * @template T - Type of listing that must have a providerId property
 * @param listings - Array of listings to compute compliance for
 * @returns Array of listings with reactive compliance data added
 */
export function useListingsCompliance<T extends { providerId?: number | null, standardStatus?: StandardStatus | null }>(
  listings: readonly T[] | null | undefined
): readonly (T & { compliance: { complianceRequirement: ComplianceRequirement; shouldScramble: boolean; hideSquareFootage: boolean }})[] {
  const { user, acceptedTerms, acceptedBrowserBasedTerms } = useUserContext();

  return useMemo(() => {
    if (!listings || listings.length === 0) {
      return [];
    }

    return listings.map(listing => {
      const providerId = listing.providerId;

      const complianceRequirement = !providerId
        ? ComplianceRequirement.NONE
        : getComplianceRequirementFromCookies(providerId, user?.id);

      const shouldScramble = shouldScrambleForProvider(providerId, complianceRequirement);

      return {
        ...listing,
        compliance: {
          complianceRequirement,
          shouldScramble,
          hideSquareFootage: getHideSquareFootage(providerId, listing.standardStatus),
        },
      };
    });
  }, [listings, user?.id, acceptedTerms, acceptedBrowserBasedTerms]);
}

